import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './styleMobile.css'
import { FaPlus } from "react-icons/fa6";
import { FaMinus, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { MdFastfood } from "react-icons/md";
import SafeImage from '../../components/SafeImage';
import imgPizza from '../../assets/img/pizza.png';
import ModalAddObsItemCarrinho from "../../components/ModalAddObsItemCarrinho";
import useCarrinho from "../../hooks/useCarrinho";
import { useTranslation } from 'react-i18next';
import { formatPriceWithSymbol } from '../../utils/currency';
import Money from '../../components/Money';
import { syncCarrinhoBackend } from "../../services/api";
import { getCartSessionToken } from '../../utils/cartSession';

const Carrinho = () => {
    const { t } = useTranslation('cardapio');
    const { nomeEmpresaForUrl, idEmpresa, slug } = useParams();
    const identifier = slug || idEmpresa;
    const isSlugRoute = !!slug;

    // 🔧 FIX: buildUrl com fallback para sessionStorage
    const buildUrl = (subpath) => {
        if (isSlugRoute && slug) {
            return `/c/${slug}/${subpath}`;
        } else if (nomeEmpresaForUrl && idEmpresa) {
            return `/cardapio/${nomeEmpresaForUrl}/${idEmpresa}/${subpath}`;
        }
        // Fallback: buscar do sessionStorage
        try {
            const empresaData = sessionStorage.getItem('sessionCompany');
            if (empresaData) {
                const empresa = JSON.parse(empresaData);
                if (empresa?.slug) {
                    return `/c/${empresa.slug}/${subpath}`;
                } else if (empresa?.name && empresa?.id_empresa) {
                    const nomeForUrl = empresa.name.toLowerCase().replace(/\s+/g, '-');
                    return `/cardapio/${nomeForUrl}/${empresa.id_empresa}/${subpath}`;
                }
            }
        } catch (e) {
            console.error('[CARRINHO] Erro ao construir URL:', e);
        }
        // Último fallback - retorna URL com undefined (vai falhar mas pelo menos logou)
        console.warn('[CARRINHO] buildUrl: Não foi possível construir URL válida para:', subpath);
        return `/cardapio/${nomeEmpresaForUrl}/${idEmpresa}/${subpath}`;
    };

    // Obtém o external_id da URL atual, se existir
    const [searchParams] = useSearchParams();
    const externalId = searchParams.get('external_id');

    const navigate = useNavigate();

    // 🔧 Obter id_empresa do sessionStorage quando usando rota slug
    const getIdEmpresa = () => {
        if (idEmpresa) return idEmpresa;
        try {
            const empresaData = sessionStorage.getItem('sessionCompany');
            if (empresaData) {
                const empresa = JSON.parse(empresaData);
                return empresa?.id_empresa;
            }
        } catch (e) {
            console.error('[CARRINHO] Erro ao obter id_empresa do sessionStorage:', e);
        }
        return null;
    };
    const effectiveIdEmpresa = getIdEmpresa();

    // ✅ Usar hook useCarrinho para sincronização com backend
    const {
        itensCarrinho,
        setItensCarrinho,
        incrementarQuantidade,
        decrementarQuantidade,
        limparCarrinho,
        atualizarPasso
    } = useCarrinho(effectiveIdEmpresa, {
        syncWithBackend: !!effectiveIdEmpresa, // Só sincroniza se tiver id_empresa válido
        trackAbandonment: !!effectiveIdEmpresa
    });

    // Alias para compatibilidade
    const carrinho = itensCarrinho;

    // 🛒 TRACKING DE ABANDONO: espelhar o carrinho no backend ao mudar (debounced).
    // Cobre ajustes de quantidade/remoção na tela do carrinho. Usa o mesmo token do
    // pedido (session_token_pedeja) → sem falso abandono ao finalizar. Fire-and-forget.
    useEffect(() => {
        if (!effectiveIdEmpresa || !Array.isArray(itensCarrinho) || itensCarrinho.length === 0) return;
        const cartToken = getCartSessionToken();
        const timer = setTimeout(() => {
            syncCarrinhoBackend(effectiveIdEmpresa, itensCarrinho, 'viu_carrinho', cartToken).catch(() => { /* não bloquear UX */ });
        }, 1200);
        return () => clearTimeout(timer);
    }, [itensCarrinho, effectiveIdEmpresa]);

    const handleBack = () => {
        // Atualizar passo para tracking de abandono (só se tiver id_empresa)
        if (effectiveIdEmpresa) {
            atualizarPasso('cardapio');
        }

        // 🔧 FIX: Usar sessionStorage como fallback quando params da URL estão undefined
        let baseUrl;
        if (isSlugRoute && slug) {
            baseUrl = `/c/${slug}`;
        } else if (nomeEmpresaForUrl && idEmpresa) {
            baseUrl = `/cardapio/${nomeEmpresaForUrl}/${idEmpresa}`;
        } else {
            // Fallback: buscar do sessionStorage
            try {
                const empresaData = sessionStorage.getItem('sessionCompany');
                if (empresaData) {
                    const empresa = JSON.parse(empresaData);
                    if (empresa?.slug) {
                        baseUrl = `/c/${empresa.slug}`;
                    } else if (empresa?.name && empresa?.id_empresa) {
                        const nomeForUrl = empresa.name.toLowerCase().replace(/\s+/g, '-');
                        baseUrl = `/cardapio/${nomeForUrl}/${empresa.id_empresa}`;
                    }
                }
            } catch (e) {
                console.error('[CARRINHO] Erro ao obter dados da empresa:', e);
            }
            // Se ainda não tiver baseUrl, usa history.back()
            if (!baseUrl) {
                console.warn('[CARRINHO] Usando history.back() como fallback');
                navigate(-1);
                return;
            }
        }

        if (externalId) {
            navigate(`${baseUrl}?external_id=${externalId}`);
        } else {
            navigate(baseUrl);
        }
    };

    const [showModalAddObsItem, setShowModalAddObsItem] = useState(false);
    const [currentEditingItem, setCurrentEditingItem] = useState(null);

    const showToastEmptyKart = () => {
        toast(t('cart.empty'), { autoClose: 3000, type: "warning" })
    }


    // ✅ Usar funções do hook useCarrinho para decrement/increment
    const handleDecrement = (id, index) => {
        decrementarQuantidade(id, index);
    };


    const handleAvancar = () => {
        // Atualizar passo para tracking de abandono
        atualizarPasso('finalizar_pedido');

        // 🛒 Espelhar o passo no carrinho rastreado (token do pedido) → popula "Passo do Abandono"
        if (effectiveIdEmpresa && Array.isArray(itensCarrinho) && itensCarrinho.length > 0) {
            const cartToken = getCartSessionToken();
            syncCarrinhoBackend(effectiveIdEmpresa, itensCarrinho, 'finalizar_pedido', cartToken).catch(() => { /* não bloquear navegação */ });
        }

        // Navega para a página de finalizar pedido, com ou sem o external_id
        navigate(
            externalId
                ? `${buildUrl('finalizar-pedido')}?external_id=${externalId}`
                : buildUrl('finalizar-pedido')
        );
    }

    const handleOpenModalAddObsItem = (item) => {
        setShowModalAddObsItem(true);
        //setCurrentEditingItemId(item.id); // Continua sendo útil se precisar desse ID para outras lógicas.
        const currentItem = itensCarrinho.find(i => i.id === item.id);
        setCurrentEditingItem(currentItem); // Armazenando diretamente o item que será editado.
    };

    const handleIncrement = (id, index) => {
        incrementarQuantidade(id, index);
    };

    // ✅ calcularValorAdicionais agora vem do hook useCarrinho

    /*const obtemValorItem = (valorItemEncrypted, quantidade) => {
        const decryptedPrice = CryptoJS.AES.decrypt(valorItemEncrypted, secretKey).toString(CryptoJS.enc.Utf8);
        const valorTotalItem = quantidade * parseFloat(decryptedPrice);
        let stringDescryptedPrice = valorTotalItem.toFixed(2).toString().replace('.', ',');
        return stringDescryptedPrice;
    }

    const calcularValorTotal = (itens) => {
        let total = 0;
        itens.forEach(item => {
            const valorItem = obtemValorItem(item.price, item.quantity);
            const valorItemFloat = parseFloat(valorItem.replace(',', '.'));
            total += valorItemFloat;
        });
        return total.toFixed(2).toString().replace('.', ',');
    }*/
    const obtemValorItem = (valorItem, quantidade) => {
        //process.env.REACT_APP_ISDEVELOP === 'true' && console.log("valor item", valorItem);
        const valorTotalItem = typeof valorItem === 'number' ? valorItem : parseFloat(valorItem) || 0;
        return valorTotalItem;
    };

    const calcularValorTotal = (itens) => {
        let total = 0;
        itens.forEach(item => {
            //process.env.REACT_APP_ISDEVELOP === 'true' && console.log("item:", JSON.stringify(item))
            const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
            total += itemTotal;
        });
        return total;
    };

    // ✅ Pedido mínimo — obter do sessionStorage
    const getMinimumOrder = () => {
        try {
            const data = sessionStorage.getItem('sessionCompany');
            if (data) return JSON.parse(data)?.pedido_minimo || 0;
        } catch (e) {}
        return 0;
    };
    const pedidoMinimo = getMinimumOrder();
    const totalNumerico = calcularValorTotal(carrinho) || 0;
    const faltante = pedidoMinimo > 0 ? Math.max(0, pedidoMinimo - totalNumerico) : 0;
    const atingiuMinimo = pedidoMinimo <= 0 || totalNumerico >= pedidoMinimo;
    const porcentagemMinimo = pedidoMinimo > 0 ? Math.min(100, (totalNumerico / pedidoMinimo) * 100) : 100;

    // ✅ Usar limparCarrinho do hook useCarrinho
    const clearItensCarrinho = () => {
        limparCarrinho();
    };
    

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return (

        <div className="carrinho-page">
            <div className="carrinho-header">
                <div className="carrinho-header-back" onClick={() => handleBack()}>
                    <svg xmlns="http://www.w3.org/2000/svg" width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </div>
                <span className="carrinho-header-title">{t('cart.title')}</span>
                {carrinho.length >= 2 && (
                    <span className="carrinho-header-clear" onClick={() => clearItensCarrinho()}>
                        <FaRegTrashAlt size={13} /> {t('cart.clear')}
                    </span>
                )}
            </div>

            <div className="carrinhoContainer">

                <ModalAddObsItemCarrinho
                    currentItem={currentEditingItem}
                    showModalAddObsItem={showModalAddObsItem}
                    setShowModalAddObsItem={setShowModalAddObsItem}
                    itensCarrinho={itensCarrinho}
                    setItensCarrinho={setItensCarrinho}
                    setCurrentEditingItem={setCurrentEditingItem}
                />

                {
                    itensCarrinho && itensCarrinho.map((item, index) => (
                        <div style={{ marginBottom: "12px" }} key={item.id}>
                            <div className="itemNoCarrinho">
                                <div className="wrapItensCarrinho">
                                    <div className="rowItensStart">
                                        <div className="imgItemCarrinho">
                                            <SafeImage
                                                src={item?.image}
                                                alt={t('cart.itemImageAlt')}
                                                fallback={
                                                    item?.type === "pizza"
                                                        ? <img src={imgPizza} alt={t('cart.itemImageAlt')} />
                                                        : <div className="icon-fallback"><MdFastfood style={{ fontSize: 48, color: '#666' }} /></div>
                                                }
                                            />
                                        </div>
                                        <div className="infoItemCarrinho">
                                            <span className="itemTitleCarrinho">{item.quantity}x {item.title}</span>
                                            <span className="itemPriceCarrinho"><Money value={obtemValorItem(item.total, item.quantity)} /></span>
                                            {item.grupos_adicionais_selecionados && item.grupos_adicionais_selecionados.map((grupoAdicional, index) => (
                                                <div className="containerAdicionalInfoCarrinho" key={index}>
                                                    <span>{grupoAdicional.grupoTitle}</span>
                                                    <div className="divAdicionalInfoCarrinho">
                                                        {grupoAdicional.adicionais.map((itemAdicional, index) => (
                                                            <span key={index}>
                                                                <span style={{ color: "#333" }}>
                                                                    {grupoAdicional.calcular_maior_valor || grupoAdicional.calcular_media
                                                                        ? (itemAdicional.quantity * item.quantity) > grupoAdicional.adicionais.length
                                                                            ? `${grupoAdicional.adicionais.length}/${grupoAdicional.adicionais.length}`
                                                                            : `${itemAdicional.quantity * item.quantity}/${grupoAdicional.adicionais.length}`
                                                                        : `x${itemAdicional.quantity * item.quantity}`
                                                                    }
                                                                </span>
                                                                {" "}
                                                                {itemAdicional.title}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="itemAdicionaisCarrinho"></div>
                                            <span className="itemObsCarrinho" onClick={() => handleOpenModalAddObsItem(item)}>
                                                {item.observacoes_item
                                                    ? item.observacoes_item
                                                    : t('cart.addObservation')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="controlQtdItem">
                                        <div className="borderBoxQtdCarrinho">
                                            <div className="btnsForQtd">
                                                <div className="downAndCounter">
                                                    <button onClick={() => handleDecrement(item.id, index)}>
                                                        {item.quantity === 1 ?
                                                            <div className="trashIconCarrinho">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width={18}
                                                                    height={18}
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="#808080"
                                                                    strokeWidth={2}
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    className="feather feather-trash-2"
                                                                >
                                                                    <polyline points="3 6 5 6 21 6" />
                                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                                    <line x1={10} y1={11} x2={10} y2={17} />
                                                                    <line x1={14} y1={11} x2={14} y2={17} />
                                                                </svg>
                                                            </div>
                                                            :
                                                            <div className="trashIconCarrinho">
                                                                <FaMinus style={{ color: "grey", fontSize: "16px" }} />
                                                            </div>
                                                        }

                                                    </button>
                                                    <div className="counterQtdItemCarrinho">{item.quantity}</div>
                                                </div>
                                                <button className="btnAddCounter" onClick={() => handleIncrement(item.id, index)}>
                                                    <p><FaPlus style={{ fontSize: '20px' }} /></p>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                }

                <div className="btnAdicionarItens">
                    <button onClick={() => handleBack()}>
                        <span>{t('cart.addMoreItems')}</span>
                    </button>
                </div>
            </div>

            {/* Banner de pedido mínimo */}
            {pedidoMinimo > 0 && carrinho.length > 0 && !atingiuMinimo && (
                <div style={{
                    padding: '10px 16px',
                    backgroundColor: '#FFF8E1',
                    borderTop: '1px solid #FFE082',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                }}>
                    <span style={{ fontSize: '13px', color: '#F57F17', fontWeight: 500 }}>
                        {t('cart.minimumOrderProgress', {
                            minimum: formatPriceWithSymbol(pedidoMinimo),
                            remaining: formatPriceWithSymbol(faltante)
                        })}
                    </span>
                    <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: '#FFE082',
                        borderRadius: '3px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${porcentagemMinimo}%`,
                            height: '100%',
                            backgroundColor: porcentagemMinimo >= 100 ? '#4CAF50' : '#FF9800',
                            borderRadius: '3px',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                </div>
            )}

            {carrinho.length > 0 && atingiuMinimo ?
                <div className="avancarDetalhe">
                    <button onClick={() => handleAvancar()} className="btnAvancarDetalhe">
                        <p style={{ marginBottom: "0px" }}>{t('cart.advance')}</p>
                        <div className="btndata">{formatPriceWithSymbol(calcularValorTotal(carrinho))}</div>
                    </button>
                </div>
                : carrinho.length > 0 && !atingiuMinimo ?
                <div className="avancarDetalhe">
                    <button
                        className="btnAvancarDetalhe"
                        style={{ background: "#f59e0b", color: "#fff" }}
                        onClick={() => toast.warning(t('cart.minimumOrderNotReached', {
                            remaining: formatPriceWithSymbol(faltante),
                            minimum: formatPriceWithSymbol(pedidoMinimo)
                        }), { toastId: 'minimum-order-warning' })}
                    >
                        <p style={{ marginBottom: "0px" }}>{t('cart.advance')}</p>
                        <div className="btndata" style={{ color: '#fff' }}>{formatPriceWithSymbol(calcularValorTotal(carrinho))}</div>
                    </button>
                </div>
                :
                <div className="avancarDetalhe">
                    <button className="btnAvancarDetalhe" style={{ background: "#eee", color: "#777" }} onClick={() => showToastEmptyKart()}>
                        <p style={{ marginBottom: "0px" }}>{t('cart.advance')}</p>
                        <div className="btndata" style={{ color: '#777' }}>{formatPriceWithSymbol(calcularValorTotal(carrinho))}</div>
                    </button>
                </div>
            }

        </div>

    );
}

export default Carrinho;